// Angular
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  Input,
} from '@angular/core';
import {
  ControlContainer,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GoogleMapsModule, MapGeocoder } from '@angular/google-maps';
// PrimeNG
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SkeletonModule } from 'primeng/skeleton';
// Rxjs
import { MessageService } from 'primeng/api';
import { filter, map } from 'rxjs';
// Environment
import { environment } from '@env/environment';
// Components
import { AddressInputComponent } from '@shared/components-v3/atomic/address-input/address-input.component';
import { NumberInputComponent } from '@shared/components-v3/atomic/number-input/number-input.component';
import { MapComponentV2 } from '../../blocks/map-v2/map-v2.component';
// Services
import { GeolocationApiService } from '@core/services-v2/geolocation/geolocation-api.service';
import { getAddressFromGeocoderResult } from './services/address-map.utils';
import { removeSpecialCharacters } from './utils';
// Directives
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
import { OptionalLabelDirective } from '@shared/directives-v3/optional-label/optional-label.directive';
// Models
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { STREET_TYPES } from '@core/constants/street-type-es.constant';

const NG_MODULES = [ReactiveFormsModule, GoogleMapsModule, CommonModule];
const PRIME_MODULES = [
  InputTextModule,
  InputTextareaModule,
  SkeletonModule,
  DropdownModule,
];
const COMPONENTS = [
  AddressInputComponent,
  NumberInputComponent,
  MapComponentV2,
];
const DIRECTIVES = [
  ControlErrorsDirective,
  FormSubmitDirective,
  OptionalLabelDirective,
];

export interface AddressForm {
  search: string | null;
  street: string;
  number: number;
  latitude: number | null;
  longitude: number | null;
  reference: string | null;
  //Start:: Revisar
  commune: string;
  region: string;
  locality: string;
  departmentOrHouse: string;
  //End:: Revisar
  /*city: string;
  province: string;*/

  streetType: string;
  postalCode: string;

  contactDocumentId: string;
  contactName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhone: string;
}

/*export interface AddressFormCL {
  search: string | null;
  street: string;
  number: number;
  latitude: number | null;
  longitude: number | null;
  reference: string | null;
  //Start:: Revisar
  commune: string;
  departmentOrHouse: string;
  //End:: Revisar
}*/

export type AddressFormInit = {
  [K in keyof AddressForm]: AddressForm[K] | null;
};

/*export type AddressFormInitCL = {
  [K in keyof AddressFormCL]: AddressFormCL[K] | null;
};

export type AddressFormInitPE = {
  [K in keyof AddressFormCL]: AddressFormCL[K] | null;
};*/

@Component({
  selector: 'app-address-form-control-container',
  standalone: true,
  imports: [NG_MODULES, COMPONENTS, PRIME_MODULES, DIRECTIVES],
  templateUrl: './address-form-control-container.component.html',
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
})
export class AddressFormControlContainerComponent implements OnInit {
  //#region ViewChild components
  @ViewChild(MapComponentV2) mapComponent!: MapComponentV2;
  //#endregion

  //#region Inputs & Outputs
  @Input() useMap: boolean = true;
  //#endregion

  // Tipo pensando que el address estará directamente en el form.
  parentForm!: FormGroup<ControlsOf<AddressFormInit>>; //FormGroup<ControlsOf<AddressFormInit>>;
  /*parentFormCL!: FormGroup<ControlsOf<AddressFormInitCL>>;
  parentFormPE!: FormGroup<ControlsOf<AddressFormInitPE>>;*/
  isLoadingCommunes = signal<boolean>(true);
  referenceHelperText = environment.helperTexts.reference;
  isEnabledDepartmentInput = !environment.disabledInputs.department;
  countryCode = environment.country;

  // Corregir...
  communes = signal<any[]>([]);
  //Perú
  departamentos = signal<any[]>([]);
  provincias = signal<any[]>([]);
  distritos = signal<any[]>([]);

  //#region Getters
  get searchField(): FormControl<string | null> {
    return this.parentForm.controls.search;
  }

  get streetField(): FormControl<string | null> {
    return this.parentForm.controls.street;
  }

  get numberField(): FormControl<number | null> {
    return this.parentForm.controls.number;
  }

  get communeField(): FormControl<string | null> {
    return this.parentForm.controls.commune;
  }
  //#endregion

  //#region Dependencies
  private readonly controlContainer = inject(ControlContainer);
  private readonly messageService = inject(MessageService);
  private readonly geocoder = inject(MapGeocoder);
  private readonly geolocationApiService = inject(GeolocationApiService);
  //#endregion

  ngOnInit(): void {
    this.parentForm = this.controlContainer.control as FormGroup;
    if (this.countryCode === 'cl') this.getAdministrativeAreasLevel2();
    else this.getAdministrativeAreasLevel1();
    this.setFormGroup();
  }

  soloNumeros(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  validateNIF(nif?: string | null): boolean {
    const clean = (nif ?? '').toUpperCase().trim();
    if (!clean || !/^\d{8}[A-Z]$/.test(clean)) return false;
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    return letters[parseInt(clean.slice(0, 8), 10) % 23] === clean[8];
  }

  onNifBlur(): void {
    const ctrl = this.parentForm.get('contactDocumentId')!;
    const raw = ctrl.value;
    if (this.validateNIF(raw)) {
      ctrl.setErrors(null);
    } else {
      ctrl.setErrors({ invalidNif: true });
    }
    ctrl.markAsTouched();
  }

  /**
   * Inicializa y configura los controles del formulario principal para la dirección.
   * Añade los siguientes campos si no existen:
   * - search: Campo de búsqueda (opcional)
   * - street: Calle (requerido)
   * - number: Número (requerido)
   * - commune: Comuna (requerido)
   * - region: Región (opcional)
   * - locality: Localidad (requerido)
   * - latitude: Latitud (opcional)
   * - longitude: Longitud (opcional)
   * - departmentOrHouse: Departamento (opcional)
   * - reference: Referencia (opcional)
   *
   * @private
   * @returns {void}
   */
  private setFormGroup(): void {
    if (!this.parentForm.contains('search')) {
      this.parentForm.addControl('search', new FormControl(null));
    }

    if (!this.parentForm.contains('street')) {
      this.parentForm.addControl(
        'street',
        new FormControl(null, {
          nonNullable: true,
          validators: [Validators.required],
        })
      );
    }

    if (!this.parentForm.contains('number')) {
      this.parentForm.addControl(
        'number',
        new FormControl(null, {
          nonNullable: true,
          validators: [Validators.required],
        })
      );
    }
    if (!this.parentForm.contains('latitude')) {
      this.parentForm.addControl('latitude', new FormControl(null));
    }
    if (!this.parentForm.contains('longitude')) {
      this.parentForm.addControl('longitude', new FormControl(null));
    }
    if (!this.parentForm.contains('reference')) {
      this.parentForm.addControl('reference', new FormControl(null));
    }

    if (!this.parentForm.contains('commune')) {
      const validators =
        this.countryCode === 'es' ? [] : [Validators.required];
      this.parentForm.addControl(
        'commune',
        new FormControl(null, {
          nonNullable: true,
          validators,
        })
      );
    }
    // Hace que sea requerido solo para Perú
    if (!this.parentForm.contains('region')) {
      const validators =
        this.countryCode === 'pe' ? [Validators.required] : [];
      this.parentForm.addControl('region', new FormControl(null, validators));
    }
    if (!this.parentForm.contains('locality')) {
      const validators =
        this.countryCode === 'pe' ? [Validators.required] : [];
      this.parentForm.addControl(
        'locality',
        new FormControl(null, validators)
      );
    }
    if (!this.parentForm.contains('departmentOrHouse')) {
      this.parentForm.addControl('departmentOrHouse', new FormControl(null));
    }

    if (!this.parentForm.contains('streetType')) {
      this.parentForm.addControl('streetType', new FormControl(null));
    }
    if (!this.parentForm.contains('postalCode')) {
      this.parentForm.addControl('postalCode', new FormControl(null));
    }

    //if (this.countryCode === 'cl') this.setFormGroupCL();

    if (!this.parentForm.contains('documentId')) {
      this.parentForm.addControl('contactDocumentId', new FormControl(null));
    }
    if (!this.parentForm.contains('name')) {
      this.parentForm.addControl('contactName', new FormControl(null));
    }
    if (!this.parentForm.contains('lastName')) {
      this.parentForm.addControl('contactLastName', new FormControl(null));
    }
    if (!this.parentForm.contains('email')) {
      this.parentForm.addControl('contactEmail', new FormControl(null));
    }
    if (!this.parentForm.contains('phone')) {
      this.parentForm.addControl('contactPhone', new FormControl(null));
    }
  }

  streetTypes = STREET_TYPES;

  /*
  private setFormGroupCL(): void {
    if (!this.parentForm.contains('departmentOrHouse')) {
      this.parentForm.addControl('departmentOrHouse', new FormControl(null));
    }
  }*/

  /**
   * Al arrastrar el marcador en el mapa, obtener la dirección de la ubicación y setear los campos del formulario.
   * @param event - Evento de arrastre del marcador.
   * @returns
   */
  onDragEnd(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) {
      this.resetAddressForm();
      this.messageService.add({
        severity: 'error',
        summary: 'Error al obtener la dirección',
        detail: 'No se pudo obtener la dirección de la ubicación.',
      });
      return;
    }

    this.getGeocode({ location: event.latLng }).subscribe((geocoderResult) => {
      if (!geocoderResult) {
        this.resetAddressForm();
        this.messageService.add({
          severity: 'error',
          summary: 'Error al obtener la dirección',
          detail: 'No se pudo obtener la dirección de la ubicación.',
        });
        return;
      }
      this.setAddressForm(geocoderResult, true);
    });
  }

  /**
   * Limpiar formulario de dirección.
   */
  private resetAddressForm(): void {
    this.parentForm.patchValue({
      search: null,
      street: null,
      streetType: null,
      postalCode: null,
      number: null,
      commune: null,
      region: null,
      locality: null,
      latitude: null,
      longitude: null,
    });
  }

  /**
   * Setea la localidad en el formulario a partir de la comuna.
   * - Localidad que tenga el mismo nombre que la comuna.
   */
  /*private setLocality(localities: any[], commune: string) {
    const formmatedCommune = removeSpecialCharacters(commune);
    if (!formmatedCommune) return;
    const itemLocality = localities.find(
      (item) => removeSpecialCharacters(item.location) === formmatedCommune
    );
    if (!itemLocality) return;
    this.parentForm.controls['locality'].setValue(itemLocality.location);
  }*/

  setAddressManually(): void {
    /*if (
      !this.streetField?.value ||
      !this.numberField?.value ||
      !this.communeField?.value
    )
      return;*/
  }

  /**
   * Setear la localidad en el formulario a partir de la comuna.
   * - Utilizado solo en Chile.
   * @returns
   */
  onSelectCommune(): void {
    /*  if (!this.communeField?.value) return;
    const communeItem = this.communes().find(
      (commune) => commune.id === this.communeField?.value
    );
    if (!communeItem) return;*/
    //this.setLocality(communeItem.localities, communeItem.city);
  }

  /**
   * Setear la dirección en el formulario de dirección y setear el marcador en la nueva ubicación.
   * @param placeId - Identificador de lugar de Google Maps.
   */
  setAddress(placeId: string): void {
    this.getGeocode({ placeId }).subscribe((geocoderResult) => {
      //if (!geocoderResult) return;
      // FIXME: Revisar si se debe setear el search field.
      if (!geocoderResult) {
        this.resetAddressForm();
        return;
      }
      this.setAddressForm(geocoderResult);
      this.mapComponent.setMarker(geocoderResult.geometry.location.toJSON());
    });
  }

  private getGeocode({
    placeId,
    location,
  }: {
    placeId?: string;
    location?: google.maps.LatLng;
  }) {
    if (!placeId && !location) {
      throw new Error('PlaceId or location is required.');
    }
    const params = placeId ? { placeId } : { location };
    return this.geocoder.geocode(params).pipe(
      filter(
        ({ status, results }) =>
          status === google.maps.GeocoderStatus.OK && !!results.length
      ),
      map(({ results }) => {
        const response = results.filter(
          (value) =>
            value.types.includes('street_address') ||
            value.types.includes('premise')
        );
        return response.length ? response[0] : null;
      })
    );
  }

  /**
   * Setea dirección en el formulario a partir del resultado de la geolocalización.
   * @param geocoderResult - Resultado de la geolocalización.
   * @param setSearchField - Indica si se debe setear el campo de búsqueda.
   */
  private setAddressForm(
    geocoderResult: google.maps.GeocoderResult,
    setSearchField?: boolean
  ): void {
    const { search, ...address } =
      getAddressFromGeocoderResult(geocoderResult);
    /* const { lat, lng } = geocoderResult.geometry.location.toJSON();
    const addressComponents = geocoderResult.address_components;
    const street = this.getAddressItem(addressComponents, 'route');
    console.log('street: ', street);
    const number = this.getAddressItem(addressComponents, 'street_number');

    /*const commune = this.getAddressItem(
      addressComponents,
      'administrative_area_level_3'
    );*/
    // En Chile es comuna y en Perú es provincia.
    /*const administrativeAreaLevel2 = this.getAddressItem(
      addressComponents,
      'administrative_area_level_2'
    );
    console.log('administrativeAreaLevel2: ', administrativeAreaLevel2);
    const region = this.getAddressItem(
      addressComponents,
      'administrative_area_level_1'
    );
    console.log('region: ', region);
    const locality = this.getAddressItem(addressComponents, 'locality');
    console.log('locality: ', locality);
    const country = this.getAddressItem(addressComponents, 'country');
    console.log('country: ', country);
    const formattedStreet = [street, number].filter(Boolean).join(' ');
    const formmatedCommune =
      locality === administrativeAreaLevel2
        ? [locality]
        : [locality, administrativeAreaLevel2];
    console.log('formmatedCommune: ', formmatedCommune);
    const formattedAddress = [formattedStreet, ...formmatedCommune, country]
      .filter(Boolean)
      .join(', ');
    console.log('formattedAddress: ', formattedAddress);
    const communeId = this.getCommuneByGoogleCommune(administrativeAreaLevel2);
    const formFieldValues = {
      street,
      number: +number,
      //locality,
      commune: communeId,
      latitude: lat,
      longitude: lng,
      region,
    };
    console.log('setSearchField: ', setSearchField);
    const address = setSearchField
      ? { search: formattedAddress, ...formFieldValues }
      : formFieldValues;*/

    //this.parentForm.patchValue(address);

    if (this.countryCode === 'es') {
      const tipoVia = address.street?.split(' ')[0];
      const tipoViaMatch = this.streetTypes.find(
        (tipo) => tipo.label.toLowerCase() === tipoVia?.toLowerCase()
      );
      this.parentForm.patchValue({
        streetType: tipoViaMatch?.value || null,
        postalCode: address.postalCode || null,
      });
    }

    const communeId = this.getCommuneIdByGoogleAdministrativeAreaLevel2(
      address.administrativeAreaLevel3
    );
    const params: Partial<AddressFormInit> = {
      street: address.street,
      number: address.number,
      commune: communeId,
      region: address.administrativeAreaLevel1,
      locality: address.locality,
      latitude: address.latitude,
      longitude: address.longitude,
    };
    if (setSearchField) params.search = search;
    this.parentForm.patchValue(params);
  }

  /**
   * Obtener áreas administrativas de nivel 2 (comunas en Chile o provincias en Perú).
   */
  private getAdministrativeAreasLevel2(): void {
    this.geolocationApiService
      .getAdministrativeAreasLevel2()
      .subscribe((administrativeAreas) => {
        this.communes.set(administrativeAreas);
        this.isLoadingCommunes.set(false);
      });
  }

  /**
   * Obtener id de comuna de Implementos a partir de la comuna de Google Maps y setear la localidad
   * - Utilizado solo en Chile.
   * @param value
   * @returns
   */
  private getCommuneIdByGoogleAdministrativeAreaLevel2(value: string): string {
    if (!value) return '';
    const formmatedValue = removeSpecialCharacters(value);
    const communeItem = this.communes().find(
      (commune) => removeSpecialCharacters(commune.city) === formmatedValue
    );
    if (!communeItem) return '';
    //this.setLocality(communeItem.localities, value);
    return communeItem.id;
  }

  /***********************************************************************************
   * Métodos de Perú.
   ***********************************************************************************/
  isLoadingDepartamentos = signal<boolean>(true);
  isLoadingProvincias = signal<boolean>(false);
  isLoadingDistritos = signal<boolean>(false);

  /**
   * Obtener áreas administrativas de nivel 1 (region en Chile o departamentos en Perú).
   */
  private getAdministrativeAreasLevel1() {
    this.geolocationApiService.getListaDepartamentos().subscribe({
      next: (administrativeAreas: any) => {
        this.departamentos.set(administrativeAreas);
        this.isLoadingDepartamentos.set(false);
      },
    });
  }

  /**
   * Al seleccionar nivel 1.
   * @param event
   */
  onSelectDepartamento({ value }: DropdownChangeEvent): void {
    this.isLoadingProvincias.set(true);
    const selectedDepartamento = this.departamentos().find(
      (departamento) => departamento.nombre === value
    );
    //utilizamos el campo department, ya que por ahora no lo utilizan
    // this.addressForm?.controls['department'].setValue(
    //   selectedDepartamento.codigo
    // );
    this.parentForm?.controls['departmentOrHouse'].setValue(
      selectedDepartamento.codigo
    );
    this.geolocationApiService
      .getListaProvincia(selectedDepartamento.codigo)
      .subscribe({
        next: (data: any) => {
          this.provincias.set(data);
          this.distritos.set([]);
          this.isLoadingProvincias.set(false);
          this.parentForm.patchValue({
            commune: '',
            locality: '',
          });
        },
      });
  }

  /**
   * Al seleccionar nivel 2.
   * @param event
   */
  onSelectProvincia(event: DropdownChangeEvent): void {
    this.isLoadingDistritos.set(true);
    const selectedProvincia = this.provincias().find(
      (provincia) => provincia.nombre === event.value
    );
    this.geolocationApiService
      .getListaDistrito(selectedProvincia.codigo)
      .subscribe({
        next: (data: any) => {
          this.distritos.set(data);
          this.isLoadingDistritos.set(false);
          this.parentForm.patchValue({
            locality: '',
          });
        },
      });
  }

  setDepartamentoManually(): void {
    /*if (!this.communeField.value) return;
    const communeItem = this.communes().find(
      (commune) => commune.id === this.communeField.value
    );
    if (!communeItem) return;
    this.setLocality(communeItem.localities, communeItem.city);*/
  }
}
